import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { authoritativeGetItem, getItem, removeItem, setItem } from 'owa-local-storage';
import { LazyBootModule, LazyImport } from 'owa-bundling-light';

const RECURRING_DEVICE_MIN_DISTINCT_DAYS = 2;
const OFFLINE_DATABASE_NAME_PREFIX = 'owa-offline-data';
const lazyDatabaseManager = new LazyImport(
    new LazyBootModule(() => import('owa-database-manager'), {
        name: 'OfflineDeviceDatabaseManager',
    }),
    module => module
);

let deviceVisitGeneration = 0;
let authoritativeRecurringDevice = false;

interface DeviceVisits {
    days: number;
    lastDay: string;
}

/**
 * Records that this browser profile was used today, so offline sync only runs on recurring
 * devices. localStorage is wiped for incognito/private sessions and typically for kiosks, so those
 * never accumulate distinct days. The ripcord is only enforced in isOfflineSyncAvailable.
 * Errors are handled internally.
 */
export function recordDeviceVisit(): void {
    void recordDeviceVisitAsync().catch(() => {
        // Best effort; offline availability checks fall back to treating the device as new
    });
}

async function recordDeviceVisitAsync(): Promise<void> {
    const generation = deviceVisitGeneration;
    if (!isHostAppFeatureEnabled('localCacheAllowed')) {
        return;
    }
    const visits = readDeviceVisits();
    if (isRecurring(visits)) {
        return;
    }
    // Use the browser's local calendar day (not UTC) so a visit near UTC midnight can't be
    // miscounted as two distinct days.
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
    // Devices with an offline database predate the recurring-device requirement; keep their cache
    const hasOfflineDatabase = await hasExistingOfflineDatabase();
    if (generation !== deviceVisitGeneration) {
        return;
    }
    if (hasOfflineDatabase) {
        writeDeviceVisits({ days: RECURRING_DEVICE_MIN_DISTINCT_DAYS, lastDay: todayKey });
    } else if (visits?.lastDay !== todayKey) {
        writeDeviceVisits({ days: (visits?.days ?? 0) + 1, lastDay: todayKey });
    }
}

/** Forgets the device history so the device has to become recurring again. */
export function clearDeviceVisits(): void {
    deviceVisitGeneration++;
    authoritativeRecurringDevice = false;
    removeItem(self, 'offlineDeviceVisits');
}

export function isRecurringDevice(): boolean {
    return authoritativeRecurringDevice || isRecurring(readDeviceVisits());
}

/**
 * Checks the live marker and existing offline databases before destructive worker decisions,
 * even if bootstrap's marker write is still pending.
 */
export async function isRecurringDeviceAuthoritative(): Promise<boolean> {
    const generation = deviceVisitGeneration;
    const recurring =
        isRecurring(await readDeviceVisitsAuthoritative()) ||
        (isHostAppFeatureEnabled('localCacheAllowed') && (await hasExistingOfflineDatabase()));
    if (generation !== deviceVisitGeneration) {
        return false;
    }
    // Keep downstream synchronous worker gates consistent with this live check.
    authoritativeRecurringDevice = recurring;
    return recurring;
}

function isRecurring(visits: DeviceVisits | undefined): boolean {
    return (visits?.days ?? 0) >= RECURRING_DEVICE_MIN_DISTINCT_DAYS;
}

function parseDeviceVisits(raw: string | null): DeviceVisits | undefined {
    try {
        const parsed = raw ? JSON.parse(raw) : undefined;
        if (typeof parsed?.days === 'number' && typeof parsed?.lastDay === 'string') {
            return parsed;
        }
    } catch {
        // Corrupt marker is treated as a new device
    }
    return undefined;
}

function readDeviceVisits(): DeviceVisits | undefined {
    return parseDeviceVisits(getItem(self, 'offlineDeviceVisits'));
}

async function readDeviceVisitsAuthoritative(): Promise<DeviceVisits | undefined> {
    return parseDeviceVisits(await authoritativeGetItem('offlineDeviceVisits'));
}

function writeDeviceVisits(visits: DeviceVisits): void {
    setItem(self, 'offlineDeviceVisits', JSON.stringify(visits));
}

async function hasExistingOfflineDatabase(): Promise<boolean> {
    const { getDatabaseNames } = await lazyDatabaseManager.import();
    const names = await getDatabaseNames();
    return names.some(name => name.startsWith(OFFLINE_DATABASE_NAME_PREFIX));
}
